import { type BuilderInfo } from "helpers/AppProvider.types";

import { BTLocalStorage } from "types/btStorage";

/**
 * A user's personal preference for how Bills open: as a modal over the list, or as a full page.
 * Layered on top of the org-wide `billsFullPageConversion` flag — see {@link resolveBillViewMode}.
 */
export type BillViewModePreference = "modal" | "fullPage";

/** Reads the stored preference, defaulting to "modal" when unset so the org-wide flag rollout doesn't force full page on anyone. */
export function getBillViewModePreference(): BillViewModePreference {
    return BTLocalStorage.get("bt-string-billViewModePreference") ?? "modal";
}

export function setBillViewModePreference(preference: BillViewModePreference): void {
    BTLocalStorage.set("bt-string-billViewModePreference", preference);
}

/**
 * Resolves whether a Bill should open as a full page. The personal preference only takes effect
 * for builders whose org already has `billsFullPageConversion` enabled; otherwise Bills stay modal.
 *
 * Pure so it can be called both from React tree code and from the plain-parameter grid-column
 * factories that receive a boolean rather than reading context.
 */
export function resolveBillViewMode(
    builderInfo: BuilderInfo | null | undefined,
    preference: BillViewModePreference,
): boolean {
    return (builderInfo?.flags?.billsFullPageConversion ?? false) && preference === "fullPage";
}
